import React, { useContext, useEffect, useState } from "react";
import { ConfirmDialog, Dropdown, Text, DraggableCheckboxList, CheckboxListItem, depascalize } from "sprint-common-web";

import { FileRecordListingContext } from "../context/FileRecordListingContext";
import {
    ColumnConfig,
    FileRecordColumn,
    FileRecordListingConfig,
    sortColumnsByConfiguredOrder
} from "./FileRecordListingConfig";

interface EditFileRecordListingColumnsConfigDialogProps {
    pageSize: number;
    setPageSize(pageSize: number): void;
    onClose(): void;
}

export const EditFileRecordListingColumnsConfigDialog: React.FC<EditFileRecordListingColumnsConfigDialogProps> = (
    props: EditFileRecordListingColumnsConfigDialogProps
) => {
    const [items, setItems] = useState<CheckboxListItem[]>([]);
    const [pageSize, setPageSize] = useState<number>(props.pageSize);

    const { listingConfig, saveListingConfig } = useContext(FileRecordListingContext);

    useEffect(() => {
        const checkboxListItems = getCheckboxListItems(listingConfig);

        setItems(checkboxListItems);
    }, []);

    const handleConfirm = () => {
        props.setPageSize(pageSize);

        const config = mapToListingConfig(items, pageSize);

        saveListingConfig(config);

        props.onClose();
    };

    const handlePageSizeChange = (key: string) => {
        setPageSize(Number(key));
    };

    return (
        <ConfirmDialog
            title={"Listing configuration"}
            text={""}
            confirm={{
                onClick: handleConfirm
            }}
            cancel={{
                onClick: props.onClose
            }}
            width={700}
            contentHeight={400}
        >
            <div style={{ display: "flex" }}>
                <div style={{ width: "50%", float: "left", left: 0, paddingRight: 20 }}>
                    <Text value="Page size" styles={{ root: { paddingBottom: 10 } }} />
                    <Dropdown
                        options={pageSizeDropDownOptions.map((key) => ({ key, text: key }))}
                        selectedKey={String(pageSize)}
                        onChange={handlePageSizeChange}
                    />
                </div>
                <div style={{ width: "50%", float: "right" }}>
                    <Text value="Columns" styles={{ root: { paddingBottom: 10 } }} />
                    <DraggableCheckboxList items={items} setItems={setItems} />
                </div>
            </div>
        </ConfirmDialog>
    );
};

const pageSizeDropDownOptions: string[] = ["10", "20", "50", "100"];

const isItemDisabled = (key: FileRecordColumn) => {
    return key === FileRecordColumn.Actions;
};

const hiddenColumns = [FileRecordColumn.Link];

const getCheckboxListItems = (config: FileRecordListingConfig): CheckboxListItem[] => {
    return sortColumnsByConfiguredOrder(config.columns)
        .filter(([key]) => !hiddenColumns.includes(Number(key)))
        .map(([key, column]) => {
            const resolvedKey = Number(key);

            return {
                key: resolvedKey,
                text: normalizeColumnName(depascalize(FileRecordColumn[resolvedKey])),
                checked: column.visible,
                disabled: isItemDisabled(resolvedKey)
            };
        });
};

const mapToListingConfig = (columnItems: CheckboxListItem[], pageSize: number): FileRecordListingConfig => {
    const columns = {} as Record<FileRecordColumn, ColumnConfig>;

    let order = 0;

    for (const item of columnItems) {
        const index = Number(item.key) as FileRecordColumn;
        columns[index] = { order, visible: item.checked };

        order++;
    }

    return {
        pageSize,
        columns
    };
};

const normalizeColumnName = (name: string) =>
    name
        .split(" ")
        .map((word, idx) => (idx === 0 ? word : word.toLocaleLowerCase()))
        .join(" ");
